<template>
	<SidePanelContent :type="SidebarContent.PRODUCT_HISTORY" with-close-bar hide-on-click-outside class="w-[75vw]">
		<template #heading>
			<div class="flex items-center gap-04">
				<PortalIcon v-if="portal" class="headerPortalIcon" :portal="portal" /> {{ skuHumanName }} <I18n path="history.pastEvents" />
			</div>
		</template>

		<Loading v-if="isActive('productHistory')" absolutely-centered></Loading>

		<div v-else :class="[`grid h-full grid-rows-[auto_auto_1fr]`, { 'min-w-[70rem]': areThereAnyRecords }]">
			<div v-if="bestPerformingEvent" :class="['font-body-s-bold mb-03 mt-05']">
				<I18n path="history.bestPerformingEvent" />

				<div class="bestPerformingEvent font-body-s mr-03 mt-03 py-02">
					<div class="overflow-hidden px-04">
						<TooltipIfEllipsis :tooltip-text="bestPerformingEvent.eventName">{{ bestPerformingEvent.eventName }}</TooltipIfEllipsis>
					</div>
					<div class="px-04">{{ dateTimeFormattedShort(bestPerformingEvent.startDate) }}</div>
					<div class="px-04">{{ dateTimeFormattedShort(bestPerformingEvent.endDate) }}</div>
					<div class="px-04">{{ formatDays(bestPerformingEvent.promoLength) }}</div>
					<div class="px-04">{{ bestPerformingEvent.discountDepth }}%</div>
					<div class="cell-align-right px-04">{{ formatUnits(bestPerformingEvent.units) }}</div>
					<div class="cell-align-right px-04">{{ formatCurrency(bestPerformingEvent.baseline) }}</div>
					<div class="cell-align-right overflow-hidden px-04 text-secondary-orange">
						<TooltipIfEllipsis :tooltip-text="formatCurrency(bestPerformingEvent.revenue)" placement="top">
							<span class="font-body-s-medium">{{ formatCurrency(bestPerformingEvent.revenue) }}</span>
						</TooltipIfEllipsis>
					</div>

					<div class="cell-align-right px-04">
						<template v-if="upliftFormat === '$'">
							<span class="text-message-success">{{ formatCurrency(bestPerformingEvent.uplift as number) }}</span>
						</template>

						<div v-else class="text-message-success">{{ formatScore(bestPerformingEvent.upliftScore) }}</div>
					</div>
				</div>
			</div>

			<div v-if="areThereAnyRecords" :class="['font-body-s-bold mb-03 mt-05']">
				<I18n path="history.allEvents" />
			</div>

			<Table
				v-model:sort-column="sortColumn"
				v-model:sort-descending="sortDescending"
				:columns="columns"
				:items="items"
				theme="highlighted"
				height="small"
				:class="{ 'mt-06': !areThereAnyRecords }"
				@click.stop
			>
				<template #header(uplift)>
					<div class="mr-03"><I18n path="history.uplift" /></div>
					<Tooltip>
						<ValueSwitch v-model="upliftFormat" value-left="score" value-right="$" @update:model-value="toggleUpliftFormat" @click.stop>
							<template #valueLeft><Icon name="star-outline" /></template>
						</ValueSwitch>

						<template #content>
							<T path="upliftTooltipDesc">
								<template #scoreSymbol>
									<span class="-ml-02 flex"> <Icon name="star-outline" /> {{ t('upliftScoreDesc') }} </span></template
								>
							</T>
						</template>
					</Tooltip>
				</template>

				<template #cell(eventName)="{ item }">
					<div v-if="item.major && portal" :class="['majorSaleIcon flex-center -ml-04 mr-03 flex h-full', `portal--${portal}`]">
						<PortalIcon class="icon" :portal="portal" />
					</div>

					<div class="flex h-full w-full flex-1 items-center overflow-hidden">
						<TooltipIfEllipsis :tooltip-text="item.eventName as string" placement="top">
							{{ item.eventName }}
						</TooltipIfEllipsis>
					</div>
				</template>

				<template #cell(startDate)="{ item }">
					{{ dateTimeFormattedShort(item.startDate as DateTime) }}
				</template>

				<template #cell(endDate)="{ item }">
					{{ dateTimeFormattedShort(item.endDate as DateTime) }}
				</template>

				<template #cell(promoLength)="{ item }"> {{ formatDays(item.promoLength as number) }} </template>

				<template #cell(discountDepth)="{ value }"> {{ value }}% </template>

				<template #cell(units)="{ value }">
					<TooltipIfEllipsis :tooltip-text="formatUnits(value as number)" placement="top">
						{{ formatUnits(value as number) }}
					</TooltipIfEllipsis>
				</template>
				<template #cell(baseline)="{ value }">
					<TooltipIfEllipsis :tooltip-text="formatCurrency(value as number)" placement="top">
						{{ formatCurrency(value as number) }}
					</TooltipIfEllipsis>
				</template>
				<template #cell(revenue)="{ value }">
					<TooltipIfEllipsis class="text-secondary-orange" :tooltip-text="formatCurrency(value as number)" placement="top">
						<span class="font-body-s-medium">{{ formatCurrency(value as number) }}</span>
					</TooltipIfEllipsis>
				</template>
				<template #cell(uplift)="{ item }">
					<template v-if="upliftFormat === '$'">
						<TooltipIfEllipsis class="text-message-success" :tooltip-text="formatCurrency(item.uplift as number)" placement="top">
							{{ formatCurrency(item.uplift as number) }}
						</TooltipIfEllipsis>
					</template>

					<div v-else class="text-message-success">{{ formatScore(item.upliftScore as number) }}</div>
				</template>
			</Table>
		</div>

		<template v-if="isEmbedded && isDashboardDiscountEnabled && productName" #footer>
			<div class="flex items-center justify-end">
				<div class="font-caption mr-06 text-ink-60">Discover even more with the new Discount Analytics Dashboard</div>

				<Button icon="open-in-new" variant="secondary" size="s" class="openDashboardButton" @click="openDiscountsDashboard"
					>Open Dashboard</Button
				>
			</div>
		</template>
	</SidePanelContent>
</template>

<script setup lang="ts">
import {
	Button,
	PortalIcon,
	Tooltip,
	type TableColumn,
	ValueSwitch,
	Table,
	TooltipIfEllipsis,
	useTranslation,
	Icon,
	I18n,
	Loading
} from '@bluebrick/saas-ui'
import { onClickOutside } from '@vueuse/core'
import { type DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, toRefs, watch } from 'vue'
import { dateTimeFormattedShort } from '@/common/dateUtils'
import { formatCurrency, formatUnits, getSortAlphabeticallyByFieldComparator, StoreToPortalMap } from '@/common/utils'
import { useAppStore } from '@/features/app/useAppStore'
import { fetchProductHistory } from '@/features/eventDetails/eventDetailsRemoteRepository'
import { mapHistoryEventToDomain } from '@/features/eventDetails/eventDetailsUtils'
import { emitToParent } from '@/features/iframe/useParentEmit'
import { useLoadersStore } from '@/features/loaders/useLoadersStore'
import type { UpliftFormat } from '@/features/preferences/types'
import { usePreferencesStore } from '@/features/preferences/usePreferencesStore'
import SidePanelContent from '@/features/sidePanel/components/SidePanelContent.vue'
import { useSidebarStore } from '@/features/sidePanel/useSidebarStore'
import { useSkusStore } from '@/features/skus/useSkusStore'
import { useStoresStore } from '@/features/stores/useStoresStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { useFeatureFlagStore } from '@/features/user/useFeatureFlagStore'
import T from '@/translations/T.vue'
import { SidebarContent, type HistoryEvent, type HistoryTableItem, Portal } from '@/types'

type TableColumnWithDataType = TableColumn & {
	type?: 'text' | 'date' | 'number'
}

const { selectedHistorySKUOption, isDashboardDiscountEnabled } = toRefs(useSidebarStore())
const { closeSidebar } = useSidebarStore()
const preferencesStore = usePreferencesStore()
const { setUpliftFormat } = preferencesStore
const { upliftFormat } = storeToRefs(preferencesStore)
const { isActive } = useLoadersStore()
const { trackEvent } = useTelemetry()
const { isEmbedded } = storeToRefs(useAppStore())
const { shouldAnonymizeData } = storeToRefs(useFeatureFlagStore())
const { stores } = storeToRefs(useStoresStore())
const { t } = useTranslation()
const { skusBySkuId } = storeToRefs(useSkusStore())

const target = ref(null)

function close(viaCloseBar = false) {
	trackEvent('user', 'Closed history sidebar.', { viaCloseBar })
	closeSidebar(SidebarContent.PRODUCT_HISTORY)
}

const history = ref<HistoryEvent[]>([])
const items = ref<HistoryTableItem[]>([])

const areThereAnyRecords = computed(() => items.value.length > 0)

const bestPerformingEvent = computed(() =>
	items.value.reduce<HistoryTableItem | undefined>((acc, item) => {
		if (acc === undefined || item.upliftScore > acc.upliftScore) {
			acc = item
		}

		return acc
	}, undefined)
)

const sortColumn = ref<Columns>('startDate')
const sortDescending = ref(true)

const activeStore = computed(() => stores.value.find((store) => store.id === selectedHistorySKUOption.value?.storeId))
const storeId = computed(() => activeStore.value?.id)
const skuId = computed(() => selectedHistorySKUOption.value?.uniqueSkuId)
const skuHumanName = computed(() => selectedHistorySKUOption.value?.humanName)
const productName = computed(() => (skuId.value ? skusBySkuId.value[skuId.value]?.product_name : undefined))

onClickOutside(target, () => close())

onMounted(async () => {
	if (!storeId.value || !skuId.value) {
		return
	}

	const historyEvents = await fetchProductHistory(storeId.value, skuId.value)

	if (!historyEvents) {
		return
	}

	history.value = historyEvents

	items.value = history.value?.map((el) => mapHistoryEventToDomain(el, shouldAnonymizeData.value))

	sortItemsByDate()
})

type Columns = 'eventName' | 'startDate' | 'discountDepth' | 'units' | 'baseline' | 'revenue' | 'uplift' | 'upliftScore'

const columns: TableColumnWithDataType[] = [
	{ key: 'eventName', label: t('history.eventName'), sortable: true, width: '10rem' },
	{ key: 'startDate', label: t('history.startDate'), sortable: true, type: 'date' },
	{ key: 'endDate', label: t('history.endDate'), sortable: true, type: 'date' },
	{ key: 'promoLength', label: t('history.duration'), sortable: true, type: 'number' },
	{ key: 'discountDepth', label: t('history.discountPercentage'), sortable: true, type: 'number', width: '2rem' },
	{ key: 'units', label: t('history.units'), sortable: true, type: 'number', align: 'right' },
	{ key: 'baseline', label: t('history.baseline'), sortable: true, type: 'number', align: 'right' },
	{ key: 'revenue', label: t('history.revenue'), sortable: true, type: 'number', align: 'right' },
	{ key: 'uplift', label: t('history.uplift'), sortable: true, type: 'number', align: 'right' }
]
const columnsByKey = columns.reduce<Record<string, TableColumnWithDataType>>((result, column) => {
	result[column.key] = column

	return result
}, {})

const portal = computed(() => activeStore.value?.id && StoreToPortalMap.get(activeStore.value?.id))

function toggleUpliftFormat(format: UpliftFormat) {
	trackEvent('user', 'Changed uplift format.', { newFormat: format })

	setUpliftFormat(format)
}

function openDiscountsDashboard() {
	const steamDiscountsDashboardId = '7d43685b-2c7d-475b-94c3-4e6118a46b49'
	const otherDiscountsDashboardId = '0e1dda8e-bd26-4dea-891a-053706d77bea'

	const payload = {
		id: portal.value === Portal.STEAM ? steamDiscountsDashboardId : otherDiscountsDashboardId,
		filters: {
			Single_Store: portal.value === Portal.STEAM ? undefined : activeStore.value?.displayName,
			Single_Product: productName.value,
			Single_ProductName: productName.value
		}
	}

	trackEvent('user', 'Clicked dashboards redirect.', { data: JSON.stringify(payload) })

	emitToParent('OPEN_DASHBOARD', payload)
}

function sortItems(comparatorFunction: (a: HistoryTableItem, b: HistoryTableItem) => number) {
	const currentItems = [...items.value]

	items.value = currentItems.sort(comparatorFunction)
}

function sortItemsByDate() {
	sortItems((a, b) => {
		if (sortDescending.value) {
			return b.startDate.toMillis() - a.startDate.toMillis()
		}

		return a.startDate.toMillis() - b.startDate.toMillis()
	})
}

function sortItemsByNumber() {
	let columnName = sortColumn.value

	if (sortColumn.value === 'uplift' && upliftFormat.value === 'score') {
		columnName = 'upliftScore'
	}

	sortItems((a, b) => {
		if (sortDescending.value) {
			return Number(b[columnName]) - Number(a[columnName])
		}

		return Number(a[columnName]) - Number(b[columnName])
	})
}

function sortItemsByText() {
	sortItems(getSortAlphabeticallyByFieldComparator(sortColumn.value, sortDescending.value))
}

function formatDays(durationInDays: number) {
	return `${durationInDays} ${t('history.days', { count: durationInDays })}`
}

function formatScore(score: number) {
	return score.toFixed(2)
}

watch([sortColumn, sortDescending, upliftFormat], () => {
	if (columnsByKey[sortColumn.value]?.type === 'date') {
		sortItemsByDate()
	} else if (columnsByKey[sortColumn.value]?.type === 'number') {
		sortItemsByNumber()
	} else {
		sortItemsByText()
	}
})
</script>

<style scoped lang="scss">
.ProductHistory {
	height: 100vh;
	width: 75vw;
	display: grid;
	grid-template-rows: auto auto 1fr;
}

.majorSaleIcon {
	width: 1.875rem;

	.icon {
		width: 1rem;
		height: 1rem;
		:deep(svg) {
			fill: theme('textColor.strong');
		}
	}

	$portals: 'STEAM' 'META' 'MICROSOFT' 'PLAYSTATION' 'NINTENDO' 'HUMBLE' 'GOG' 'EPIC' 'APPLE' 'GOOGLE';

	@each $portal in $portals {
		&.portal--#{$portal} {
			background-color: var(--color-portal-#{$portal}-primary);
		}
	}
}

.openDashboardButton {
	flex-direction: row-reverse;

	:deep(.prefixIcon) {
		margin-right: -0.25rem;
		margin-left: 0.5rem;
	}
}

.headerPortalIcon {
	width: 1.5rem;
	height: 1.5rem;
}

.bestPerformingEvent {
	border: 0.0625rem solid theme('colors.level.03');
	display: grid;
	// Those fractions are proportions of elements in the history table
	grid-template-columns:
		172fr
		119fr
		115fr
		114fr
		129fr
		93fr
		111fr
		113fr
		147fr;
}

.cell-align-right {
	@apply flex w-full justify-end;
}
</style>
